import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Paper,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CommonButton from "../../../Components/InputComponents/CommonButton";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { axiosAPI } from "../../../Components/AxiosComponents/Axios";
import { decryptId } from "../../../Components/CryptoComponent";

// ----------------- TRANSFORM HELPERS -----------------
const transformProcedureData = (procedure) => {
  return procedure.stages.map((stage) => {
    if (stage.subProcedures?.length > 0) {
      return {
        title: stage.RPS_Name,
        subTabs: stage.subProcedures.map((sub) => ({
          title: sub.RSP_Name,
          fields: sub.fields.map(mapFieldToTableRow),
        })),
      };
    } else {
      return {
        title: stage.RPS_Name,
        fields: stage.fields.map(mapFieldToTableRow),
      };
    }
  });
};

const mapFieldToTableRow = (field) => ({
  name: field.RPF_Name,
  type: field.RPF_Type?.toLowerCase() || "-",
  required: field.RPF_IsRequired === 1 ? "Yes" : "-",
  maxLength: field.RPF_MaxLength || "-",
  expiry: "No", // You can customize this based on your logic
  options: getSelectOptions(field),
  expiryPeriod: field?.RPF_ExpiryPeriod,
});

const getSelectOptions = (field) => {
  if (field.RPF_Type === "Dropdown") {
    try {
      const parsed = JSON.parse(field.RPF_ValidValues || "[]");
      return Array.isArray(parsed) ? parsed.join(", ") : "-";
    } catch {
      return "-";
    }
  }
  return "-";
};

// ----------------- MAIN COMPONENT -----------------
export default function Processview({ selectedProcedure, handleCloseModal }) {

  const [procedure, setProcedure] = useState({});
  const [sections, setSections] = useState([]);

  const getData = () => {
    axiosAPI
      ?.get(`/RecruitmentProcedure/listbyId/${selectedProcedure?.RP_Id}`)
      .then((res) => {
        const data = res?.data?.data;

        if (data) {
          setProcedure(data);

          const transformed = transformProcedureData(data);
          setSections(transformed);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch procedure data:", err);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
  }, []);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <CommonButton
        variant="outlined"
        background="#006CB8"
        onClick={handleCloseModal}
        margin="0px 20px 10px 0px"
      >
        <ArrowBackIcon sx={{ color: "white" }} />
      </CommonButton>

      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        mb={2}
      >
        {procedure?.RP_Name}
      </Typography>

      {sections.map((section, i) => (
        <Accordion
          key={i}
          defaultExpanded
          sx={{
            border: "1px solid lightgrey",
            boxShadow: "none",
            margin: "10px auto",

            maxWidth: `100vw`,

            overflowX: "auto",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              sx={{
                fontWeight: 600,
                fontFamily: "Geist, sans-serif",
                fontSize: "20px",
              }}
            >
              {i + 1}) {section.title}
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            {section.subTabs
              ? section.subTabs.map((subTab, j) => (
                  <Box key={j} mb={3} px={2}>
                    <Typography
                      mb={1}
                      sx={{
                        fontWeight: 500,
                        fontFamily: "Geist, sans-serif",
                        fontSize: "18px",
                      }}
                    >
                      <span style={{ fontSize: "17px" }}>
                        {i + 1}.{j + 1})
                      </span>{" "}
                      {subTab.title}
                    </Typography>
                    {renderTable(subTab.fields)}
                  </Box>
                ))
              : renderTable(section.fields)}
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );
}

// ----------------- RENDER TABLE FUNCTION -----------------
const renderTable = (fields) => {
  const typeOptions = [
    { label: "Short Text", value: "ShortText" },
    { label: "Long Text / Comments", value: "LongText" },
    { label: "Number", value: "Number" },
    { label: "Date", value: "Date" },
    { label: "Time", value: "Time" },
    { label: "Checkbox (Yes/No)", value: "Checkbox" },
    { label: "Radio", value: "Radio" },
    { label: "Dropdown with options", value: "Dropdown" },
    { label: "Attachment", value: "Attachment" },
    { label: "Completion Status", value: "IsCompleted" },
  ];

  const getTypeLabel = (type) => {
    const match = typeOptions.find(
      (opt) => opt.value.toLowerCase() === (type || "").toLowerCase()
    );
    return match ? match.label : type; // fallback to original if not found
  };

  if (!fields || fields.length === 0)
    return <Typography>No Fields available</Typography>;

  return (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          {[
            "Field Name",
            "Field Type",
            "Required",
            "Max Length",
            "Select Options",
            "Expiry Period",
          ]?.map((head, idx) => (
            <TableCell
              key={idx}
              sx={{
                fontWeight: 500,
                fontFamily: "Geist, sans-serif",
              }}
            >
              {head}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {fields
          ?.filter(
            (f) =>
              f?.type?.toLowerCase() != "iscompleted" &&
              f?.type?.toLowerCase() != "misc"
          )
          ?.map((field, index) => (
            <TableRow key={index}>
              <TableCell>{field.name}</TableCell>
              <TableCell>{getTypeLabel(field.type)}</TableCell>
              <TableCell>{field.required}</TableCell>
              <TableCell>{field.maxLength}</TableCell>

              <TableCell>
                {field.options?.length != 1
                  ? field.options
                      ?.split(",")
                      .map((value, index) => (
                        <Chip
                          key={index}
                          label={value.trim()}
                          sx={{ m: 0.5 }}
                        />
                      ))
                  : "-"}
              </TableCell>

              <TableCell>
                {field.expiryPeriod &&
                !isNaN(Number(field.expiryPeriod.toString().replace(/"/g, "")))
                  ? `${field.expiryPeriod.toString().replace(/"/g, "")} Days`
                  : "-"}
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
};
